import React, { useEffect, useRef, useState } from 'react';
import { useApp } from '../../context/AppContext';
import { examinerLaunchSettings } from '../../utils/examinerApi';
import { AircraftPicker, ExaminerError, ExaminerReadiness, useExaminer } from './ExaminerShared';
import ExaminerDocuments from './ExaminerDocuments';
import { EXAMINER_AVAILABILITY_CHANGED } from '../../utils/useExaminerVisibility';

const defaults = () => ({ aircraftId: '', examinerProfileId: 'examiner-01', mode: 'practice', retainTranscript: false, retainAudio: false });
const validSettings = settings => {
  const parsed = { ...settings, aircraftId: Number(settings.aircraftId) };
  if (!examinerLaunchSettings({ launchRevision: 1, gradedAvailable: true, launchSettings: parsed })) {
    throw new Error('Select an aircraft, enabled examiner and valid mode. Graded mode requires transcript retention.');
  }
  return parsed;
};

const LaunchFields = ({ value, onChange, profiles, aircraft, disabled }) => {
  const change = (key, next) => onChange({ ...value, [key]: next });
  return <fieldset disabled={disabled} className="examiner-profiles">
    <legend>Organization checkride launch defaults</legend>
    <AircraftPicker aircraft={aircraft} value={value.aircraftId} onChange={next => change('aircraftId', next)} />
    <label>Examiner<select required value={value.examinerProfileId} onChange={event => change('examinerProfileId', event.target.value)}>
      <option value="">Select examiner</option>
      {profiles.map(profile => <option key={profile.id} value={profile.id}>{profile.name} / {profile.voiceId}</option>)}
    </select></label>
    {!profiles.some(profile => profile.id === value.examinerProfileId) && <p role="status">Select an enabled examiner; the configured profile is unavailable.</p>}
    <label>Exam mode<select value={value.mode} onChange={event => change('mode', event.target.value)}>
      <option value="practice">Practice</option><option value="graded">Provisional graded mock</option>
    </select></label>
    <label className="examiner-check"><input type="checkbox" checked={value.retainTranscript} onChange={event => change('retainTranscript', event.target.checked)} />Retain transcript (required for graded mode)</label>
    <label className="examiner-check"><input type="checkbox" checked={value.retainAudio} onChange={event => change('retainAudio', event.target.checked)} />Retain microphone and examiner audio</label>
    <p>These are organization defaults, not personal preferences or user recording consent. Clients must explicitly consent to any configured storage before starting.</p>
  </fieldset>;
};

const JobDetails = ({ job }) => <div>
  <p role="status">Status: <strong>{job.status}</strong>; stage: {job.stage}. Updated: {job.updatedAt || 'not available'}.</p>
  {(job.errors || []).map((error, index) => <p className="examiner-error" role="alert" key={index}>{error.code}: {error.message}</p>)}
  <details><summary>Requested setup and source provenance</summary>
    <pre>{JSON.stringify({ request: job.request, provenance: job.provenance }, null, 2)}</pre>
  </details>
  <details><summary>Coverage validation and linked curricula</summary>
    <pre>{JSON.stringify({ curriculumIds: job.curriculumIds, validation: job.validation }, null, 2)}</pre>
  </details>
  {!!job.curriculumIds?.length && <p>Review every linked curriculum and source under Instructor authoring / review before publishing. Each published part starts a separate examination; no partial ACS completion claim is made.</p>}
</div>;

export default function ExaminerSetup({ canReview, onReview }) {
  const { aircraft, currentUser, currentOrg } = useApp();
  const { request, run, busy, error, forbidden } = useExaminer();
  const [config, setConfig] = useState(null);
  const [profiles, setProfiles] = useState([]);
  const [curricula, setCurricula] = useState([]);
  const [curriculaLoaded, setCurriculaLoaded] = useState(false);
  const [jobs, setJobs] = useState([]);
  const [job, setJob] = useState(null);
  const [certificate, setCertificate] = useState('private');
  const [acsUrl, setAcsUrl] = useState('');
  const [settings, setSettings] = useState(defaults);
  const [retry, setRetry] = useState(false);
  const [initializationRetry, setInitializationRetry] = useState(false);
  const [revisions, setRevisions] = useState(null);
  const [curriculumId, setCurriculumId] = useState('');
  const [launch, setLaunch] = useState(null);
  const [launchDraft, setLaunchDraft] = useState(defaults);
  const [message, setMessage] = useState('');
  const keyCache = useRef(new Map());
  const userId = currentUser?.user?.id ?? currentUser?.id;
  const orgId = currentOrg?.id ?? currentUser?.user?.orgId;
  const updateJob = async data => {
    setJob(data);
    setJobs(previous => [data, ...previous.filter(item => item.id !== data.id)]);
    if (data.curriculumIds?.some(id => !curricula.some(item => item.id === id))) {
      setCurricula(await request('/curricula'));
      setCurriculaLoaded(true);
    }
  };
  const reload = async () => {
    setConfig(await request('/config'));
    setProfiles((await request('/profiles')).filter(profile => profile.enabled));
    setCurricula(await request('/curricula'));
    setCurriculaLoaded(true);
    setJobs(await request('/setup-jobs'));
  };
  useEffect(() => { run(reload); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const refresh = () => run(reload);
    window.addEventListener(EXAMINER_AVAILABILITY_CHANGED, refresh);
    return () => window.removeEventListener(EXAMINER_AVAILABILITY_CHANGED, refresh);
  });

  const create = event => {
    event.preventDefault();
    run(async () => {
      setMessage('');
      const launchSettings = validSettings(settings);
      if (!profiles.some(profile => profile.id === launchSettings.examinerProfileId)) throw new Error('Select an enabled examiner.');
      const body = { certificate, ...launchSettings, ...(acsUrl.trim() ? { acsSourceUrl: acsUrl.trim() } : {}) };
      if (body.acsSourceUrl) {
        const url = new URL(body.acsSourceUrl);
        if (url.protocol !== 'https:' || !['faa.gov', 'www.faa.gov'].includes(url.hostname) || url.username || url.password || url.port) {
          throw new Error('Use an official FAA HTTPS PDF or HTML URL, or leave the URL blank for automatic discovery.');
        }
      }
      const serialized = JSON.stringify(body);
      const storageKey = `examiner-setup-key:${userId}:${orgId}:${serialized}`;
      let key = keyCache.current.get(serialized);
      if (!key) {
        key = sessionStorage.getItem(storageKey);
        if (!key) {
          if (!window.crypto?.getRandomValues) throw new Error('Secure random key generation is unavailable. Use a supported browser to start setup.');
          key = [...window.crypto.getRandomValues(new Uint8Array(16))].map(value => value.toString(16).padStart(2, '0')).join('');
          sessionStorage.setItem(storageKey, key);
        }
        keyCache.current.set(serialized, key);
      }
      await updateJob(await request('/setup-jobs', { method: 'POST', body, idempotencyKey: key }));
      setRetry(false); setRevisions(null);
      setMessage('Setup job submitted. Repeating the same request reuses its job; use the deliberate resume action after fixing a failed job.');
    });
  };
  const openJob = id => run(async () => {
    setRetry(false); setRevisions(null); setMessage('');
    await updateJob(await request(`/setup-jobs/${id}`));
  });
  const openLaunch = id => {
    setCurriculumId(id); setLaunch(null); setMessage('');
    if (id) run(async () => {
      const data = await request(`/curricula/${id}/launch-settings`);
      setLaunch(data); setLaunchDraft(data.launchSettings || defaults());
    });
  };
  const selected = curricula.find(item => item.id === curriculumId);
  const ownsSelected = String(selected?.created_by) === String(userId);
  const linkedJob = jobs.find(item => item.curriculumIds?.includes(curriculumId));
  const launchChanged = launch?.launchSettings && Object.keys(launch.launchSettings).some(key => String(launch.launchSettings[key]) !== String(launchDraft[key]));
  return <section aria-label="Automatic checkride setup">
    <h3>Generate checkride from ACS</h3>
    <ExaminerError error={error} />
    {message && <p role="status">{message}</p>}
    <button type="button" disabled={busy} onClick={() => run(reload)}>Reload checkride setup</button>
    <p>To take an oral, select the checkride and an aircraft from your organization in Training. These optional staff tools refresh only when opened or explicitly requested; there is no timed refresh or autosave.</p>
    <ExaminerReadiness readiness={config?.readiness} aircraft={aircraft} />
    {config?.readiness?.plans?.some(plan => plan.status === 'blocked' && !plan.jobId) && <div>
      <label className="examiner-check"><input type="checkbox" checked={initializationRetry} onChange={event => setInitializationRetry(event.target.checked)} />I corrected initialization issues and acknowledge that resuming may queue paid generation.</label>
      <button disabled={busy || !initializationRetry} onClick={() => run(async () => {
        const data = await request('/initialization/resume', { method: 'POST', body: { retryAcknowledged: true } });
        setConfig(previous => ({ ...previous, readiness: data.readiness }));
        setInitializationRetry(false);
        setMessage(`Resumed ${data.resumedPlans} initialization plans. Stopped setup jobs require their own acknowledged resume.`);
      })}>Resume blocked initialization</button>
    </div>}
    {config && !config.setupEnabled && <p role="status">Automatic setup is not enabled on the server. Existing curricula can still be configured below. Ask the API administrator to enable the setup worker and provider.</p>}
    <p>Select a certificate and aircraft. Leave the ACS URL blank to discover the current official FAA publication, or supply its PDF/HTML URL. AI generates draft source-linked coverage and cases; an independent instructor must verify, approve and publish them.</p>
    <p>Generation incurs provider costs separately from voice minutes. Current, independently reviewed aircraft-specific POH/AFM excerpts are required; missing documents produce an actionable needs_input result.</p>
    <form onSubmit={create}>
      <fieldset disabled={busy || !config?.setupEnabled || forbidden.includes('/setup-jobs')}>
        <legend>New automatic setup</legend>
        <label>Certificate<select value={certificate} onChange={event => setCertificate(event.target.value)}>
          <option value="private">Private Pilot Airplane</option><option value="instrument">Instrument Airplane</option><option value="commercial">Commercial Pilot Airplane</option>
        </select></label>
        <label>ACS PDF / HTML URL (optional)<input type="url" value={acsUrl} onChange={event => setAcsUrl(event.target.value)} placeholder="https://www.faa.gov/..." /></label>
        <LaunchFields {...{ aircraft, profiles }} value={settings} onChange={setSettings} />
        <button type="submit">Generate checkride draft</button>
      </fieldset>
    </form>
    <ExaminerDocuments {...{ aircraft, request, run, busy }} onUploaded={reload} />
    <h3>Setup jobs</h3>
    {!jobs.length && <p>No setup jobs returned.</p>}
    <ul>{jobs.map(item => <li key={item.id}>
      {item.request?.certificate || 'Checkride'} — {item.status} / {item.stage} ({item.id})
      <button type="button" disabled={busy} onClick={() => openJob(item.id)}>View setup job</button>
    </li>)}</ul>
    {job && <article aria-label="Selected setup job">
      <JobDetails job={job} />
      <button type="button" disabled={busy} onClick={() => openJob(job.id)}>Refresh selected job</button>
      {!!job.curriculumIds?.length && <button type="button" onClick={onReview}>Review generated curricula and sources</button>}
      {['failed', 'needs_input'].includes(job.status) && <div>
        <label className="examiner-check"><input type="checkbox" checked={retry} onChange={event => setRetry(event.target.checked)} />I fixed the required inputs and reviewed prior processing costs; retry may incur additional costs.</label>
        <button type="button" disabled={busy || !retry || !config?.setupEnabled} onClick={() => run(async () => {
          await updateJob(await request(`/setup-jobs/${job.id}/resume`, { method: 'POST', body: { retryAcknowledged: true } }));
          setRetry(false); setRevisions(null);
        })}>Resume setup job</button>
      </div>}
      {canReview && ['ready_for_review', 'published'].includes(job.status) && <div>
        <button type="button" disabled={busy} onClick={() => run(async () => {
          setRevisions(null);
          const result = {};
          for (const id of job.curriculumIds) {
            const data = await request(`/curricula/${id}/launch-settings`);
            if (!Number.isSafeInteger(data.revision) || data.revision < 1) throw new Error('Every linked curriculum needs saved launch settings before publishing.');
            result[id] = data.revision;
          }
          if (!Object.keys(result).length) throw new Error('No linked curricula returned for publication.');
          setRevisions(result);
        })}>Load linked publication revisions</button>
        {revisions && <div>
          <pre>{JSON.stringify(revisions, null, 2)}</pre>
          <p>Publish every linked part together only after independent source and curriculum approval. The server rechecks all review and freshness requirements.</p>
          <button type="button" disabled={busy} onClick={() => run(async () => {
            await updateJob(await request(`/setup-jobs/${job.id}/publish`, { method: 'POST', body: { revisions } }));
            setRevisions(null); setMessage('Linked checkrides published. The client catalog determines current launch readiness.');
          })}>Publish all linked checkrides</button>
        </div>}
      </div>}
    </article>}
    <h3>Configure existing curriculum</h3>
    {curriculaLoaded && !curricula.length && <p role="status">No curricula have been returned for this organization. This list contains saved curricula, not built-in certificate choices. Select a certificate and aircraft above, then generate a checkride draft. Check the setup job for missing inputs or errors; generated curricula appear here when available. You can also create a curriculum in Instructor authoring / review.</p>}
    <label>Curriculum to configure<select disabled={busy} value={curriculumId} onChange={event => openLaunch(event.target.value)}>
      <option value="">Select curriculum</option>
      {curricula.map(item => <option value={item.id} key={item.id}>{item.title} ({item.certificate})</option>)}
    </select></label>
    {launch && <form onSubmit={event => {
      event.preventDefault();
      run(async () => {
        const launchSettings = validSettings(launchDraft);
        if (!profiles.some(profile => profile.id === launchSettings.examinerProfileId)) throw new Error('Select an enabled examiner.');
        const data = await request(`/curricula/${curriculumId}/launch-settings`, { method: 'PUT', body: { revision: launch.revision, launchSettings } });
        setLaunch(data); setLaunchDraft(data.launchSettings); setRevisions(null);
        setMessage('Launch defaults saved. This checkride is unpublished until an independent instructor publishes it again.');
      });
    }}>
      <p>Revision: {launch.revision}; published: {launch.published ? 'yes' : 'no'}. Saving is a full replacement and unpublishes the checkride.</p>
      <LaunchFields {...{ aircraft, profiles }} value={launchDraft} onChange={setLaunchDraft} disabled={busy} />
      <button type="submit" disabled={busy}>Save launch defaults</button>
      <button type="button" disabled={busy} onClick={() => openLaunch(curriculumId)}>Reload launch defaults</button>
      {linkedJob ? <p>Generated linked curriculum: publish every part through its setup job, not individually.</p>
        : canReview && !ownsSelected && selected?.reviewed_at && launch.revision > 0 && <button type="button" disabled={busy || launchChanged} onClick={() => run(async () => {
          const data = await request(`/curricula/${curriculumId}/publish`, { method: 'POST', body: { revision: launch.revision } });
          setLaunch(data); setMessage('Checkride published. Clients can reload their checkride list.');
        })}>Publish reviewed checkride</button>}
    </form>}
  </section>;
}
